import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Crown, ArrowRight, CheckCircle2, Sparkles, Star,
    ChevronDown, Globe, BookOpen, Users, TrendingUp,
    Shield, Award, Lock, Plus, User, Menu, X
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { createClientComponentClient } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getEntitlements } from '@/utils/entitlements';
import HardshipApplicationForm from '@/components/HardshipApplicationForm';
import SEO from '@/components/layout/SEO';
import LanguagePicker from '@/components/ui/LanguagePicker';
import CourseDetailModal, { ELITE_COURSE_DETAILS, type CourseDetail } from '@/components/elite/CourseDetailModal';

// ── Colour tokens ────────────────────────────────────────────────────────────
const GOLD = '#C9A84C';
const GOLD_LIGHT = '#EDD68A';
const GOLD_DIM = '#7a6230';
const DARK = '#0A0A0F';
const DARK2 = '#111118';
const DARK3 = '#16161F';
const CARD = '#1A1A24';

// ── Tier data — Foundation / Intermediate / Advanced ─────────────────────────
const AGE_TIERS = [
    {
        label: 'Foundation', tier: 'foundation', badge: 'Foundation',
        subtitle: 'What elite schools teach at age 8 — elocution, etiquette, financial acumen',
        emoji: '🌱',
        color: GOLD,
        courses: [
            { name: 'Etiquette & Social Grace', modules: 4, lessons: 12 },
            { name: 'Foundational Elocution & Vocal Mechanics', modules: 4, lessons: 12 },
            { name: 'Presence & Social Poise', modules: 4, lessons: 12 },
            { name: 'Art History & Culture', modules: 4, lessons: 12 },
            { name: 'Financial Acumen', modules: 4, lessons: 12 },
            { name: 'Logic & Negotiation', modules: 2, lessons: 5 },
            { name: 'Equestrian Studies', modules: 4, lessons: 12 },
            { name: 'Classical Music & Opera', modules: 4, lessons: 12 },
        ]
    },
    {
        label: 'Intermediate', tier: 'intermediate', badge: 'Intermediate',
        subtitle: 'Age 12 at £40,000/yr schools: rhetoric, capital markets, formal debate',
        emoji: '⚜️',
        color: '#9BB5FF',
        courses: [
            { name: 'Etiquette & Social Grace', modules: 4, lessons: 12 },
            { name: 'Rhetoric & Debate', modules: 4, lessons: 12 },
            { name: 'Cultural Fluency & Context', modules: 4, lessons: 12 },
            { name: 'Capital, Markets & Enterprise', modules: 4, lessons: 12 },
            { name: 'Logic & Negotiation', modules: 4, lessons: 12 },
            { name: 'Equestrian Studies', modules: 4, lessons: 12 },
            { name: 'Classic Literature', modules: 4, lessons: 12 },
            { name: 'Presence & Command: The Art of Leadership', modules: 4, lessons: 12 },
        ]
    },
    {
        label: 'Advanced', tier: 'advanced', badge: 'Executive Mastery',
        subtitle: 'By 16, their children read a wine list and structure a negotiation',
        emoji: '👑',
        color: '#00D2FF',
        courses: [
            { name: 'Global Oratory', modules: 4, lessons: 12 },
            { name: 'The Global Citizen - Diplomacy & Protocol', modules: 4, lessons: 12 },
            { name: 'Advanced Financial Engineering & Wealth Mechanics', modules: 4, lessons: 12 },
            { name: 'Generational Wealth & The Family Office', modules: 4, lessons: 12 },
            { name: 'High Culture, Curation & The Art Market', modules: 4, lessons: 12 },
            { name: 'Bloodstock & Elite Pursuits', modules: 4, lessons: 12 },
            { name: 'Sartorial Intelligence & Social Camouflage', modules: 4, lessons: 12 },
            { name: 'Logic & Negotiation', modules: 4, lessons: 12 },
            { name: 'Wine Appreciation & Sommelier Basics', modules: 4, lessons: 12 },
            { name: 'Classical Music & Opera Appreciation', modules: 4, lessons: 12 },
            { name: 'Philanthropy & Legacy Building', modules: 4, lessons: 12 },
            { name: 'Luxury Travel & International Etiquette', modules: 4, lessons: 12 },
            { name: 'Executive Time Management & Deep Work', modules: 4, lessons: 12 },
            { name: 'Health, Nutrition & Vitality', modules: 4, lessons: 12 },
        ]
    }
];

// ── Billing interval type ─────────────────────────────────────────────────────
type BillingInterval = 'monthly' | '6month' | 'yearly';

// ── Pricing ───────────────────────────────────────────────────────────────────
interface PricingData {
    standard: { monthly: number; sixMonthly: number; yearly: number };
    elite: { monthly: number; sixMonthly: number; yearly: number };
    bundle: { monthly: number; sixMonthly: number; yearly: number };
}

interface FaqItem {
    q: string;
    a: string;
}

const FAQS: FaqItem[] = [
    {
        q: 'Who is the Elite Academy for?',
        a: 'Anyone seeking world-class social intelligence, cultural fluency, and leadership — young scholars (ages 8+), teenagers, university students, and adult self-improvers alike. Families enrol their children with full oversight; individuals and executives join directly to master negotiation, generational wealth, and high culture. Mastery tiers reflect depth and sophistication, welcoming ambitious minds of any age.'
    },
    {
        q: 'How does the AI teach these subjects?',
        a: 'Sage AI leverages detailed curriculum frameworks and 20 core concepts per lesson to lead interactive, one-on-one coaching dialogues. Learners are guided through real-world decision scenarios, intellectual debate, protocol practice, and mastery quizzes with complete progress tracking.'
    },
    {
        q: 'Can adults and executives join as an individual?',
        a: 'Yes! Click "Join as an Individual" to enrol directly without creating a family facility. You receive unrestricted access to all 30 Elite Academy courses across Foundation, Intermediate, and Advanced Mastery — ideal for executives, professionals, and lifelong learners.'
    },
    {
        q: 'Can I combine Elite Academy with the standard 84-subject curriculum?',
        a: 'Yes. The Complete Bundle gives full access to both the 84-subject academic curriculum and all 30 Elite Academy courses under a single plan, providing comprehensive excellence at exceptional value.'
    },
    {
        q: 'Are there family & sibling discounts?',
        a: 'Yes. For family accounts, each additional child is enrolled at an automatic 50% sibling discount. The discount applies immediately at checkout across monthly, 6-monthly, and annual plans.'
    },
    {
        q: 'Can I switch between plans or cancel?',
        a: 'Absolutely. You can upgrade, downgrade, or cancel your subscription at any time with a single click in your portal. Upgrades are instantly prorated so you only pay for what you use.'
    }
];

// ─────────────────────────────────────────────────────────────────────────────
export default function EliteAcademyLandingPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const supabase = createClientComponentClient();
    const { user, userProfile } = useAuth();
    const [activeTier, setActiveTier] = useState(0);
    const [detailCourse, setDetailCourse] = useState<CourseDetail | null>(null);
    const [openFaq, setOpenFaq] = useState<number | null>(null);
    const [billing, setBilling] = useState<BillingInterval>('monthly');
    const [navOpen, setNavOpen] = useState(false);
    const [pricing, setPricing] = useState<PricingData>({
        standard: { monthly: 35, sixMonthly: 189, yearly: 336 },
        elite: { monthly: 45, sixMonthly: 243, yearly: 432 },
        bundle: { monthly: 59, sixMonthly: 318, yearly: 565 },
    });

    useEffect(() => {
        async function fetchPricing() {
            const { data } = await supabase
                .from('subscription_plans')
                .select('plan_key, price_monthly, price_six_monthly, price_yearly')
                .in('plan_key', ['homeschool_single', 'edu_elite', 'edu_bundle']);
            if (data) {
                const p = { ...pricing };
                data.forEach((row: any) => {
                    const monthly = Number(row.price_monthly) || 0;
                    const sixMonthly = Number(row.price_six_monthly) || 0;
                    const yearly = Number(row.price_yearly) || 0;
                    if (row.plan_key === 'homeschool_single') p.standard = { monthly, sixMonthly, yearly };
                    if (row.plan_key === 'edu_elite') p.elite = { monthly, sixMonthly, yearly };
                    if (row.plan_key === 'edu_bundle') p.bundle = { monthly, sixMonthly, yearly };
                });
                setPricing(p);
            }
        }
        fetchPricing();
    }, [supabase]);

    /** Returns the displayed monthly-equivalent price for the chosen interval */
    const displayMonthly = (tier: keyof PricingData) => {
        const p = pricing[tier];
        if (billing === 'yearly') return Math.round(p.yearly / 12);
        if (billing === '6month') return Math.round(p.sixMonthly / 6);
        return p.monthly;
    };

    /** Returns the total billed amount label */
    const billedLabel = (tier: keyof PricingData) => {
        const p = pricing[tier];
        if (billing === 'yearly') return `Billed $${p.yearly}/year`;
        if (billing === '6month') return `Billed $${p.sixMonthly}/6 months`;
        return null;
    };

    const savingsLabel = () => {
        if (billing === 'yearly') return 'Save 20%';
        if (billing === '6month') return 'Save 10%';
        return null;
    };

    const tier = AGE_TIERS[activeTier];
    const totalCourses = AGE_TIERS.reduce((s, t) => s + t.courses.length, 0); // 30
    const totalLessons = AGE_TIERS.reduce((s, t) => s + t.courses.reduce((cs, c) => cs + c.lessons, 0), 0);

    // Entitlements-source fix: preferences.billing.plan (webhook-written) is the
    // reliable plan signal, not active_plan (legacy). A paid parent whose plan
    // lives only in preferences was slipping through to the full wizard.
    const isExistingParent = userProfile?.facility_role === 'admin' &&
        (userProfile?.facility_type === 'homeschool' || getEntitlements(userProfile).isPaid);

    const enrolPath = (plan: string) => isExistingParent ? '/homeschool/subscription' : `/homeschool/enrol?plan=${plan}`;
    const individualPath = (plan: string) => `/homeschool/enrol?plan=${plan}&type=individual`;

    return (
        <div className="min-h-screen text-white font-sans overflow-x-hidden selection:bg-amber-500/30"
            style={{ background: DARK }}>
            <SEO
                title="Elite Academy - Learn What Billionaire Children Learn"
                description="30 elite courses across etiquette, oratory, financial intelligence, high culture, and legacy thinking. Delivered by Sage AI."
                url="https://stillwatersacademy.com/"
            />

            {/* ── NAV ─────────────────────────────────────────────────────────────── */}
            <nav className="sticky top-0 z-50 border-b border-white/5"
                style={{ background: 'rgba(10,10,15,0.90)', backdropFilter: 'blur(24px)' }}>
                <div className="max-w-7xl mx-auto px-4 md:px-12 h-16 flex items-center justify-between">
                    {/* Logo — always visible */}
                    <Link to="/" onClick={() => setNavOpen(false)}
                        className="flex items-center gap-3 shrink-0 group">
                        <div className="w-10 h-10 flex items-center justify-center" style={{ flexShrink: 0 }}>
                            <img src="/icons/blue-sage-logo.png" alt="Still Waters" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </div>
                        <div className="leading-none">
                            <span className="font-black text-[15px] tracking-tight text-white whitespace-nowrap">Still Waters</span>
                            <span className="block text-[8px] uppercase tracking-[0.25em] font-bold whitespace-nowrap"
                                style={{ color: GOLD }}>Elite Academy</span>
                        </div>
                    </Link>

                    {/* Desktop centre links */}
                    <div className="hidden md:flex items-center gap-8 text-sm font-bold text-white/40">
                        <a href="#curriculum" className="hover:text-amber-400 transition-colors">{t('elite_landing.nav.curriculum', 'Curriculum')}</a>
                        <a href="#pricing" className="hover:text-amber-400 transition-colors">{t('elite_landing.nav.pricing', 'Pricing')}</a>
                        <Link to="/homeschool" className="hover:text-amber-400 transition-colors">{t('elite_landing.nav.standardSchool', 'Standard School')}</Link>
                        <Link to="/ngo" className="hover:text-amber-400 transition-colors">{t('elite.ngo_partners')}</Link>
                    </div>

                    {/* Desktop right actions */}
                    <div className="hidden md:flex items-center gap-3">
                        <LanguagePicker />
                        {user ? (
                            <Button
                                onClick={() => navigate(userProfile?.facility_role === 'admin' ? '/homeschool/parent' : '/learn')}
                                variant="outline"
                                className="h-10 px-5 text-sm font-bold rounded-xl border border-white/20 text-white/80 hover:border-amber-400/60 hover:text-white transition-all bg-white/5"
                            >
                                <User size={14} className="mr-1.5 inline" /> {t('elite_landing.nav.myDashboard', 'My Dashboard')}
                            </Button>
                        ) : (
                            <Button
                                onClick={() => navigate('/auth/login')}
                                variant="outline"
                                className="h-10 px-5 text-sm font-bold rounded-xl border border-white/20 text-white/70 hover:border-white/40 hover:text-white transition-all bg-transparent"
                            >
                                {t('elite_landing.nav.signIn', 'Sign In')}
                            </Button>
                        )}
                        <Button
                            onClick={() => navigate(enrolPath('edu_elite'))}
                            className="h-10 px-6 text-sm font-black rounded-xl border border-amber-500/40 text-white hover:border-amber-400 transition-all shadow-lg shadow-amber-500/10"
                            style={{ background: 'linear-gradient(135deg, #7a5c20, #C9A84C)' }}
                        >
                            {t('elite_landing.nav.enrolNow', 'Enrol Now')} <ArrowRight size={14} className="ml-1.5 inline" />
                        </Button>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-all"
                        onClick={() => setNavOpen(v => !v)}
                        aria-label={t('elite.toggle_navigation_menu')}
                    >
                        {navOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </nav>

            {/* Mobile nav drawer */}
            <AnimatePresence>
                {navOpen && (
                    <motion.div
                        key="mobile-nav"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18 }}
                        className="md:hidden fixed inset-x-0 top-16 z-40 border-b border-white/10"
                        style={{ background: 'rgba(10,10,15,0.97)', backdropFilter: 'blur(24px)' }}
                    >
                        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col gap-1">
                            <a href="#curriculum"
                                onClick={() => setNavOpen(false)}
                                className="text-white/60 hover:text-amber-400 font-bold text-base py-3 border-b border-white/5 transition-colors">
                                {t('elite_landing.nav.curriculum', 'Curriculum')}
                            </a>
                            <a href="#pricing"
                                onClick={() => setNavOpen(false)}
                                className="text-white/60 hover:text-amber-400 font-bold text-base py-3 border-b border-white/5 transition-colors">
                                {t('elite_landing.nav.pricing', 'Pricing')}
                            </a>
                            <Link to="/homeschool"
                                onClick={() => setNavOpen(false)}
                                className="text-white/60 hover:text-amber-400 font-bold text-base py-3 border-b border-white/5 transition-colors">
                                {t('elite_landing.nav.standardSchool', 'Standard School')}
                            </Link>
                            <Link to="/ngo"
                                onClick={() => setNavOpen(false)}
                                className="text-white/60 hover:text-amber-400 font-bold text-base py-3 border-b border-white/5 transition-colors">
                                {t('elite.ngo_partners')}
                            </Link>
                            <div className="pt-3 pb-1">
                                <LanguagePicker />
                            </div>
                            <div className="flex flex-col gap-3 pt-2 pb-4">
                                {user ? (
                                    <Button
                                        onClick={() => { navigate(userProfile?.facility_role === 'admin' ? '/homeschool/parent' : '/learn'); setNavOpen(false); }}
                                        variant="outline"
                                        className="w-full h-12 font-bold rounded-xl border border-white/20 text-white/80 hover:border-amber-400/60 hover:text-white transition-all bg-white/5"
                                    >
                                        <User size={14} className="mr-1.5 inline" /> {t('elite_landing.nav.myDashboard', 'My Dashboard')}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={() => { navigate('/auth/login'); setNavOpen(false); }}
                                        variant="outline"
                                        className="w-full h-12 font-bold rounded-xl border border-white/20 text-white/70 hover:border-white/40 hover:text-white transition-all bg-transparent"
                                    >
                                        {t('elite_landing.nav.signIn', 'Sign In')}
                                    </Button>
                                )}
                                <Button
                                    onClick={() => { navigate(enrolPath('edu_elite')); setNavOpen(false); }}
                                    className="w-full h-12 font-black rounded-xl border border-amber-500/40 text-white hover:border-amber-400 transition-all shadow-lg shadow-amber-500/10"
                                    style={{ background: 'linear-gradient(135deg, #7a5c20, #C9A84C)' }}
                                >
                                    {t('elite_landing.nav.enrolNow', 'Enrol Now')} <ArrowRight size={14} className="ml-1.5 inline" />
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── HERO ─────────────────────────────────────────────────────────────── */}
            <section className="relative pt-24 pb-32 px-6 overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120%] h-[600px] pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(201,168,76,0.12) 0%, transparent 70%)' }} />
                <div className="absolute top-40 right-0 w-96 h-96 rounded-full blur-[120px] pointer-events-none"
                    style={{ background: 'rgba(192,132,252,0.06)' }} />

                <div className="max-w-5xl mx-auto text-center relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full border text-xs font-black tracking-widest uppercase mb-8"
                        style={{ borderColor: `${GOLD}40`, color: GOLD, background: `${GOLD}10` }}
                    >
                        <Crown size={12} /> {t('elite_landing.hero.badge', "The Curriculum They Don't Teach in Public School")}
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-5xl sm:text-7xl md:text-8xl font-black tracking-tight leading-[1.0] mb-8"
                    >
                        <span className="text-white">{t('elite_landing.hero.title_part1', 'Learn What')}</span><br />
                        <span style={{ background: `linear-gradient(90deg, ${GOLD_LIGHT}, ${GOLD}, #a07820)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            {t('elite_landing.hero.title_part2', 'Billionaire Children')}
                        </span>
                        {t('elite_landing.hero.title_part3', 'Learn.') ? (
                            <>
                                <br />
                                <span className="text-white">{t('elite_landing.hero.title_part3', 'Learn.')}</span>
                            </>
                        ) : null}
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-xl text-white/50 max-w-2xl mx-auto leading-relaxed font-medium mb-12"
                    >
                        {t('elite_landing.hero.subheadline', `${totalCourses} elite courses across etiquette, oratory, financial architecture, high culture, and legacy thinking. Delivered one-on-one by Sage AI for ambitious youth, rising professionals, and lifelong learners.`)}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                    >
                        <Button
                            onClick={() => navigate(enrolPath('edu_elite'))}
                            className="h-16 px-12 text-lg font-black rounded-2xl shadow-2xl transition-all active:scale-95"
                            style={{ background: `linear-gradient(135deg, #7a5c20, ${GOLD}, #a07820)`, color: '#0A0A0F', boxShadow: `0 20px 60px ${GOLD}30` }}
                        >
                            {t('elite_landing.hero.enrolFamily', 'Enrol Your Family')} <ArrowRight className="ml-2 inline" />
                        </Button>
                        <Button
                            onClick={() => navigate(individualPath('edu_elite'))}
                            className="h-16 px-12 text-lg font-black rounded-2xl border transition-all"
                            style={{ borderColor: `${GOLD}30`, color: GOLD, background: `${GOLD}08` }}
                        >
                            <User size={18} className="mr-2 inline" /> {t('elite_landing.hero.joinIndividual', 'Join as an Individual')}
                        </Button>
                    </motion.div>

                    {/* ── Free prep entry point ── */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="flex items-center justify-center gap-2 pt-2"
                    >
                        <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.25)' }}>
                            {t('elite_landing.hero.notReady', 'Create a free account to explore first, or')}
                        </span>
                        <button
                            onClick={() => navigate('/prep')}
                            className="text-sm font-black transition-colors"
                            style={{ color: GOLD, textDecoration: 'underline', textUnderlineOffset: '3px', background: 'none', border: 'none', cursor: 'pointer' }}
                            onMouseEnter={e => (e.currentTarget.style.color = GOLD_LIGHT)}
                            onMouseLeave={e => (e.currentTarget.style.color = GOLD)}
                        >
                            {t('elite_landing.hero.tryFree', 'Try 4 free foundation courses →')}
                        </button>
                    </motion.div>

                    {/* Stats row */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="mt-20 flex flex-wrap items-center justify-center gap-12 border-t border-white/5 pt-10"
                    >
                        {[
                            { val: totalCourses.toString(), label: t('elite_landing.hero.stats.courses', 'Elite Courses') },
                            { val: totalLessons.toString() + '+', label: t('elite_landing.hero.stats.lessons', 'Interactive Lessons') },
                            { val: '3', label: t('elite_landing.hero.stats.tiers', 'Mastery Tiers') },
                            { val: '1-on-1', label: t('elite_landing.hero.stats.mentorship', 'AI Mentorship') },
                        ].map((s, i) => (
                            <div key={i} className="text-center">
                                <div className="text-4xl font-black" style={{ color: GOLD }}>{s.val}</div>
                                <div className="text-xs uppercase tracking-widest font-bold text-white/30 mt-1">{s.label}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* ── WHAT MAKES IT ELITE ──────────────────────────────────────────────── */}
            <section className="py-24 px-6" style={{ background: DARK2 }}>
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-20">
                        <p className="text-xs uppercase tracking-[0.3em] font-black mb-4" style={{ color: GOLD }}>
                            {t('elite_landing.why.eyebrow', 'Why Elite Academy')}
                        </p>
                        <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
                            {t('elite_landing.why.title', 'The Education Reserved for the Few.')}<br />
                            <span style={{ color: GOLD }}>{t('elite_landing.why.subtitle', 'Now Open to Ambitious Minds of Every Age.')}</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Globe size={28} />,
                                title: t('elite_landing.why.pillars.p1_title', 'Cultural Intelligence & High Society'),
                                desc: t('elite_landing.why.pillars.p1_desc', "Master the unspoken codes of international circles: art curation, opera, classic literature, and global protocol. Walk into any room in the world with effortless poise."),
                                color: GOLD
                            },
                            {
                                icon: <TrendingUp size={28} />,
                                title: t('elite_landing.why.pillars.p2_title', 'Elite Financial Architecture'),
                                desc: t('elite_landing.why.pillars.p2_desc', "From foundational market literacy to family offices, wealth mechanics, and alternative investments. Generational capital thinking at every stage of life."),
                                color: '#9BB5FF'
                            },
                            {
                                icon: <Award size={28} />,
                                title: t('elite_landing.why.pillars.p3_title', 'Leadership, Oratory & Debate'),
                                desc: t('elite_landing.why.pillars.p3_desc', "Logic, formal rhetoric, negotiation, and high-stakes diplomacy. Command rooms, articulate vision with eloquence, and master crucial conversations with precision."),
                                color: '#00D2FF'
                            },
                            {
                                icon: <BookOpen size={28} />,
                                title: t('elite_landing.why.pillars.p4_title', 'Three Progressive Mastery Tiers'),
                                desc: t('elite_landing.why.pillars.p4_desc', "Foundation (core poise & basics), Intermediate (command & enterprise), and Advanced (executive diplomacy & wealth). Rigorous depth crafted for youth and adults alike."),
                                color: '#4ADE80'
                            },
                            {
                                icon: <Users size={28} />,
                                title: t('elite_landing.why.pillars.p5_title', 'Deep Progress & Mastery Analytics'),
                                desc: t('elite_landing.why.pillars.p5_desc', "Full visibility into every lesson. Real-time mastery scores, concept retention logs, and comprehensive performance metrics for parents and self-directed adult learners."),
                                color: '#FB923C'
                            },
                            {
                                icon: <Shield size={28} />,
                                title: t('elite_landing.why.pillars.p6_title', 'AI-Powered Private Mentorship'),
                                desc: t('elite_landing.why.pillars.p6_desc', "No generic classrooms. Sage AI provides personalized 1-on-1 dialogue, bespoke scenario simulations, and intellectual challenge adapted to each learner's background 24/7."),
                                color: GOLD
                            }
                        ].map((item, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.07 }}
                                whileHover={{ y: -6, borderColor: `${item.color}40` }}
                                className="p-8 rounded-[2rem] border border-white/5 transition-all duration-500 group"
                                style={{ background: CARD }}
                            >
                                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 border border-white/5 group-hover:border-opacity-40 transition-all"
                                    style={{ background: `${item.color}12`, color: item.color, borderColor: `${item.color}20` }}>
                                    {item.icon}
                                </div>
                                <h3 className="text-xl font-black text-white mb-3">{item.title}</h3>
                                <p className="text-white/40 leading-relaxed font-medium text-sm">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── THE GAP: RELATIVE DEPRIVATION COMPARISON ─────────────────────────── */}
            <section className="py-28 px-6" style={{ background: DARK2 }}>
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-14">
                        <p className="text-xs uppercase tracking-[0.3em] font-black mb-4" style={{ color: GOLD }}>
                            {t('elite_landing.the_gap.eyebrow', 'Same Age. Different Curriculum.')}
                        </p>
                        <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
                            {t('elite_landing.the_gap.title', 'The Gap Is the Product.')}
                        </h2>
                        <p className="text-white/40 font-medium text-sm mt-5 max-w-2xl mx-auto">
                            {t('elite_landing.the_gap.lead', 'This is not your fault. It was never offered. But the families at the top have been quietly buying a different education — and the difference is measurable at every age.')}
                        </p>
                    </div>

                    <div className="rounded-[2rem] overflow-hidden border border-white/10" style={{ background: CARD }}>
                        {/* Header */}
                        <div className="grid grid-cols-[55px_1fr_1fr] sm:grid-cols-[75px_1fr_1fr] md:grid-cols-3 bg-white/5">
                            <div className="p-3.5 sm:p-5 md:p-6 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.15em] sm:tracking-[0.2em] text-white/30">
                                {t('elite_landing.the_gap.col_age', 'Age')}
                            </div>
                            <div className="p-3.5 sm:p-5 md:p-6 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.15em] sm:tracking-[0.2em] text-white/30">
                                {t('elite_landing.the_gap.col_public', 'Public School Teaches')}
                            </div>
                            <div className="p-3.5 sm:p-5 md:p-6 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.15em] sm:tracking-[0.2em]" style={{ color: GOLD }}>
                                {t('elite_landing.the_gap.col_elite', 'The Elite Pay For')}
                            </div>
                        </div>
                        {[
                            {
                                age: t('elite_landing.the_gap.row_8_age', '8'),
                                public: t('elite_landing.the_gap.row_8_public', 'Sit still. Follow instructions.'),
                                elite: t('elite_landing.the_gap.row_8_elite', 'Elocution, etiquette, financial acumen, formal logic')
                            },
                            {
                                age: t('elite_landing.the_gap.row_12_age', '12'),
                                public: t('elite_landing.the_gap.row_12_public', 'Standardised test preparation.'),
                                elite: t('elite_landing.the_gap.row_12_elite', 'Rhetoric & debate, capital & markets, leadership presence')
                            },
                            {
                                age: t('elite_landing.the_gap.row_16_age', '16'),
                                public: t('elite_landing.the_gap.row_16_public', 'Choose exam subjects.'),
                                elite: t('elite_landing.the_gap.row_16_elite', 'Diplomacy & protocol, generational wealth, the art market, negotiation')
                            },
                        ].map((row, ri) => (
                            <div key={ri} className="grid grid-cols-[55px_1fr_1fr] sm:grid-cols-[75px_1fr_1fr] md:grid-cols-3 border-t border-white/5" style={{ background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                                <div className="p-3.5 sm:p-5 md:p-6 text-2xl sm:text-3xl font-black" style={{ color: GOLD }}>{row.age}</div>
                                <div className="p-3.5 sm:p-5 md:p-6 text-xs sm:text-sm md:text-base text-white/40 font-medium flex items-center leading-relaxed">{row.public}</div>
                                <div className="p-3.5 sm:p-5 md:p-6 text-xs sm:text-sm md:text-base text-white font-semibold flex items-center leading-relaxed">{row.elite}</div>
                            </div>
                        ))}
                    </div>

                    <p className="text-center text-white/40 text-sm font-medium mt-10 max-w-xl mx-auto">
                        {t('elite_landing.the_gap.quote', 'None of this appears in the national curriculum. It never has. It is taught privately, at enormous cost, to the children of the families who run things.')}
                    </p>

                    <div className="mt-10 text-center">
                        <Button
                            onClick={() => navigate(enrolPath('edu_elite'))}
                            className="h-16 px-12 text-lg font-black rounded-2xl shadow-2xl transition-all active:scale-95"
                            style={{ background: `linear-gradient(135deg, #7a5c20, ${GOLD}, #a07820)`, color: '#0A0A0F', boxShadow: `0 20px 60px ${GOLD}30` }}
                        >
                            {t('elite_landing.the_gap.cta', 'Close the Gap')} <ArrowRight className="ml-2 inline" />
                        </Button>
                        <p className="text-white/35 text-sm font-medium mt-5">
                            Learning for yourself?{' '}
                            <button
                                onClick={() => navigate(individualPath('edu_elite'))}
                                className="font-bold transition-colors"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: GOLD, padding: 0, textDecoration: 'underline', textUnderlineOffset: '3px' }}
                            >
                                Join as an individual →
                            </button>
                        </p>
                    </div>
                </div>
            </section>

            {/* ── CURRICULUM BROWSER ───────────────────────────────────────────────── */}
            <section id="curriculum" className="py-24 sm:py-32 px-4 sm:px-6" style={{ background: DARK }}>
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12 sm:mb-16">
                        <p className="text-xs uppercase tracking-[0.3em] font-black mb-4" style={{ color: GOLD }}>
                            {t('elite_landing.curriculum.eyebrow', 'Full Curriculum')}
                        </p>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight">
                            {t('elite_landing.curriculum.title', `${totalCourses} Courses. 3 Mastery Tiers.`, { courses: totalCourses })}<br />
                            <span style={{ color: GOLD }}>{t('elite_landing.curriculum.subtitle', 'One Continuous Path.')}</span>
                        </h2>
                    </div>

                    {/* Tier tabs — clean 2-line layout, responsive */}
                    <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-2.5 sm:gap-3 mb-10 max-w-4xl mx-auto">
                        {AGE_TIERS.map((tItem, i) => (
                            <button
                                key={i}
                                onClick={() => setActiveTier(i)}
                                className="px-5 py-3.5 rounded-2xl font-black text-xs sm:text-sm uppercase tracking-wider transition-all border flex flex-col items-center justify-center gap-1 text-center flex-1 active:scale-95"
                                style={activeTier === i
                                    ? { background: `linear-gradient(135deg, ${GOLD_DIM}, ${GOLD})`, color: DARK, borderColor: 'transparent', boxShadow: `0 8px 32px ${GOLD}30` }
                                    : { background: CARD, color: 'rgba(255,255,255,0.4)', borderColor: 'rgba(255,255,255,0.06)' }
                                }
                            >
                                <div className="flex items-center gap-1.5">
                                    <span>{tItem.emoji}</span>
                                    <span>{t(`elite_landing.curriculum.tiers.${tItem.tier}.label`, tItem.label)}</span>
                                </div>
                                <span className={`text-[11px] font-medium normal-case tracking-normal leading-tight ${activeTier === i ? 'text-black/80 font-semibold' : 'text-white/40'}`}>
                                    {t(`elite_landing.curriculum.tiers.${tItem.tier}.subtitle`, tItem.subtitle)}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Course grid */}
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTier}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.35 }}
                            className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                        >
                            {AGE_TIERS[activeTier].courses.map((course, i) => {
                                const emojis = ['👑', '⚜️', '🎭', '📊', '🤝', '🐎', '🎵', '🎤', '🌍', '💰', '🏛️', '🎨', '✈️', '⌚', '🍷', '❤️', '📚', '🧠', '🌿'];
                                const courseEmoji = emojis[i % emojis.length];
                                return (
                                    <motion.div
                                        key={course.name}
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: i * 0.04 }}
                                        whileHover={{ y: -4, borderColor: `${AGE_TIERS[activeTier].color}40` }}
                                        onClick={() => setDetailCourse({
                                            name: course.name,
                                            tierColor: AGE_TIERS[activeTier].color,
                                            badge: AGE_TIERS[activeTier].badge,
                                            emoji: courseEmoji,
                                            modules: ELITE_COURSE_DETAILS[course.name] ?? []
                                        })}
                                        className="p-6 rounded-[1.5rem] border border-white/5 transition-all duration-300 group cursor-pointer hover:border-white/20 active:scale-[0.98]"
                                        style={{ background: CARD }}
                                    >
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                                                style={{ background: `${AGE_TIERS[activeTier].color}15` }}>
                                                {courseEmoji}
                                            </div>
                                            <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full"
                                                style={{ color: AGE_TIERS[activeTier].color, background: `${AGE_TIERS[activeTier].color}15` }}>
                                                {AGE_TIERS[activeTier].badge}
                                            </span>
                                        </div>
                                        <h4 className="font-black text-white text-base leading-snug mb-3 group-hover:text-amber-300 transition-colors">{course.name}</h4>
                                        <div className="flex items-center justify-between text-xs font-bold text-white/30">
                                            <div className="flex gap-2">
                                                <span>{t('elite_landing.curriculum.modulesCount', '{{count}} modules', { count: course.modules })}</span>
                                                <span>·</span>
                                                <span>{t('elite_landing.curriculum.lessonsCount', '{{count}} lessons', { count: course.lessons })}</span>
                                            </div>
                                            <span className="text-[11px] opacity-0 group-hover:opacity-100 transition-opacity font-semibold" style={{ color: AGE_TIERS[activeTier].color }}>
                                                {t('elite_landing.curriculum.explore', 'Explore →')}
                                            </span>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    </AnimatePresence>

                    <div className="mt-10 text-center px-4">
                        <p className="text-white/40 text-xs sm:text-sm font-medium leading-relaxed">
                            <span className="text-white/70 font-bold">{t('elite_landing.curriculum.coursesCount', '{{count}} courses', { count: AGE_TIERS[activeTier].courses.length })}</span> ·{' '}
                            <span className="text-white/70 font-bold">{t('elite_landing.curriculum.lessonsCount', '{{count}} lessons', { count: AGE_TIERS[activeTier].courses.reduce((s, c) => s + c.lessons, 0) })}</span> ·{' '}
                            <span>{t('elite_landing.curriculum.lesson_meta', 'Each lesson includes 20 core concepts and a mastery quiz')}</span>
                        </p>
                    </div>

                    {/* ── FEATURED SPOTLIGHT: GENERATIONAL WEALTH ────────── */}
                    <div className="mt-16 max-w-4xl mx-auto px-4">
                        <div
                            className="p-7 sm:p-10 rounded-[2rem] border relative overflow-hidden transition-all duration-300"
                            style={{
                                background: 'linear-gradient(180deg, #1a1500 0%, #110e00 100%)',
                                borderColor: `${GOLD}40`,
                                boxShadow: `0 20px 60px rgba(0,0,0,0.5), 0 0 40px ${GOLD}12`
                            }}
                        >
                            {/* subtle glow */}
                            <div className="absolute top-0 left-0 w-full h-full pointer-events-none"
                                style={{ background: `radial-gradient(ellipse at 20% 0%, ${GOLD}08 0%, transparent 60%)` }} />

                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 relative z-10">
                                <div>
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-3"
                                        style={{ color: GOLD, background: `${GOLD}15`, border: `1px solid ${GOLD}30` }}>
                                        <span>🏛️</span> {t('elite_landing.course_spotlight.badge', 'Featured Curriculum Spotlight')}
                                    </div>
                                    <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                                        {t('elite_landing.course_spotlight.title', 'Wealth That Outlives You.')}{' '}
                                        <span className="text-white/40 text-base sm:text-xl font-medium block sm:inline mt-1 sm:mt-0">
                                            {t('elite_landing.course_spotlight.subtitle', '(Generational Wealth & The Family Office)')}
                                        </span>
                                    </h3>
                                </div>
                                <button
                                    onClick={() => setDetailCourse({
                                        name: 'Generational Wealth & The Family Office',
                                        tierColor: GOLD,
                                        badge: 'Executive Mastery',
                                        emoji: '🏛️',
                                        modules: ELITE_COURSE_DETAILS['Generational Wealth & The Family Office'] ?? []
                                    })}
                                    className="px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider border active:scale-95 transition-all self-start sm:self-center shrink-0 flex items-center gap-1.5"
                                    style={{ color: GOLD, borderColor: `${GOLD}40`, background: `${GOLD}08` }}
                                >
                                    <span>{t('elite_landing.course_spotlight.cta', 'Explore the syllabus')}</span>
                                    <ArrowRight size={14} />
                                </button>
                            </div>

                            <div className="grid sm:grid-cols-3 gap-4 mb-6 relative z-10">
                                <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/5 flex flex-col justify-between">
                                    <div>
                                        <div className="text-xs font-black uppercase tracking-wider mb-1.5" style={{ color: GOLD }}>
                                            {t('elite_landing.course_spotlight.module_1_label', 'Module 1')}
                                        </div>
                                        <div className="text-sm font-bold text-white mb-2">
                                            {t('elite_landing.course_spotlight.module_1_title', 'The Architecture of Old Money')}
                                        </div>
                                    </div>
                                    <div className="text-xs text-white/50 leading-relaxed">
                                        {t('elite_landing.course_spotlight.module_1_desc', 'How wealth compounds across generations · The legal structures that protect family assets · Why the wealthy never retire — they transition')}
                                    </div>
                                </div>

                                <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/5 flex flex-col justify-between">
                                    <div>
                                        <div className="text-xs font-black uppercase tracking-wider mb-1.5" style={{ color: GOLD }}>
                                            {t('elite_landing.course_spotlight.module_2_label', 'Module 2')}
                                        </div>
                                        <div className="text-sm font-bold text-white mb-2">
                                            {t('elite_landing.course_spotlight.module_2_title', 'The Family Office — How It Works')}
                                        </div>
                                    </div>
                                    <div className="text-xs text-white/50 leading-relaxed">
                                        {t('elite_landing.course_spotlight.module_2_desc', 'What a family office actually does · The investment committee & governance structure · Why 3,000+ families worldwide use this model')}
                                    </div>
                                </div>

                                <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/5 flex flex-col justify-between">
                                    <div>
                                        <div className="text-xs font-black uppercase tracking-wider mb-1.5" style={{ color: GOLD }}>
                                            {t('elite_landing.course_spotlight.module_3_label', 'Module 3')}
                                        </div>
                                        <div className="text-sm font-bold text-white mb-2">
                                            {t('elite_landing.course_spotlight.module_3_title', 'The Capstone: Your 100-Year Plan')}
                                        </div>
                                    </div>
                                    <div className="text-xs text-white/50 leading-relaxed">
                                        {t('elite_landing.course_spotlight.module_3_desc', 'Writing a family constitution · Structuring inheritance before you need to · Building a legacy that outlives any single lifetime')}
                                    </div>
                                </div>
                            </div>

                            <p className="text-center sm:text-left text-xs sm:text-sm font-medium italic border-t border-white/5 pt-4 relative z-10"
                                style={{ color: `${GOLD}70` }}>
                                {t('elite_landing.course_spotlight.quote', '"The Capstone: Your 100-Year Plan" — this is the sentence that separates elite education from everything else. 20+ core concepts per lesson.')}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── PRICING ──────────────────────────────────────────────────────────── */}
            <section id="pricing" className="py-32 px-6 relative overflow-hidden" style={{ background: DARK2 }}>
                <div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(201,168,76,0.06) 0%, transparent 60%)' }} />

                <div className="max-w-6xl mx-auto relative z-10">
                    <div className="text-center mb-16">
                        <p className="text-xs uppercase tracking-[0.3em] font-black mb-4" style={{ color: GOLD }}>{t('elite_landing.pricing.eyebrow', 'Transparent Pricing')}</p>
                        <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-6">
                            {t('elite_landing.pricing.title', 'Three Clear Paths.')}
                        </h2>

                        {/* Billing toggle — 3 options */}
                        <div className="inline-flex items-center gap-1 bg-white/5 border border-white/8 rounded-2xl p-1.5">
                            {([
                                { key: 'monthly', label: t('homeschool.subscription.intervals.monthly', 'Monthly') },
                                { key: '6month', label: t('homeschool.subscription.intervals.6month', '6 Months') + ' · ' + t('elite_landing.pricing.save_10', 'Save 10%') },
                                { key: 'yearly', label: t('homeschool.subscription.intervals.yearly', 'Yearly') + ' · ' + t('elite_landing.pricing.save_20', 'Save 20%') },
                            ] as { key: BillingInterval; label: string }[]).map(b => (
                                <button
                                    key={b.key}
                                    onClick={() => setBilling(b.key)}
                                    className="h-10 px-5 rounded-xl font-black text-sm transition-all"
                                    style={billing === b.key
                                        ? { background: GOLD, color: DARK }
                                        : { color: 'rgba(255,255,255,0.3)' }
                                    }
                                >
                                    {b.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {/* Standard */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            whileHover={{ y: -8 }}
                            className="p-10 rounded-[2.5rem] border border-white/8 flex flex-col transition-all duration-500 relative"
                            style={{ background: CARD }}
                        >
                            <div className="mb-8">
                                <span className="text-[10px] uppercase tracking-[0.25em] font-black text-white/30 px-3 py-1.5 border border-white/10 rounded-full">{t('elite_landing.pricing.standard.badge', 'Standard Education')}</span>
                            </div>
                            <div className="flex items-baseline gap-2 mb-1">
                                <span className="text-6xl font-black text-white">${displayMonthly('standard')}</span>
                                <span className="text-white/25 font-bold">{t('elite_landing.pricing.mo', '/mo')}</span>
                            </div>
                            {billedLabel('standard') && <p className="text-xs text-emerald-400 font-black uppercase tracking-widest mb-6">{billedLabel('standard')}</p>}
                            <p className="text-white/40 font-medium text-sm mb-8 leading-relaxed">{t('elite_landing.pricing.standard.desc', 'Full access to the 84-subject standard academic curriculum. Ideal for homeschooling or independent academic mastery.')}</p>
                            <ul className="space-y-3 mb-10 flex-1">
                                {[
                                    t('elite_landing.pricing.standard.f1', '84 academic subjects'),
                                    t('elite_landing.pricing.standard.f2', 'Progressive learning stages'),
                                    t('elite_landing.pricing.standard.f3', 'Parent & learner dashboard'),
                                    t('elite_landing.pricing.standard.f4', 'Mastery retention quizzes'),
                                    t('elite_landing.pricing.standard.f5', 'Official completion certificates')
                                ].map(f => (
                                    <li key={f} className="flex items-center gap-3 text-sm text-white/50 font-medium">
                                        <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <Button
                                onClick={() => navigate(enrolPath('homeschool_single'))}
                                className="w-full h-14 rounded-2xl font-black text-base border border-white/10 hover:border-white/20 text-white/70 hover:text-white transition-all"
                                style={{ background: 'rgba(255,255,255,0.04)' }}
                            >
                                {t('elite_landing.pricing.standard.cta', 'Choose Standard')}
                            </Button>
                        </motion.div>

                        {/* Elite Academy — highlighted */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            whileHover={{ y: -8 }}
                            className="p-10 rounded-[2.5rem] flex flex-col transition-all duration-500 relative overflow-hidden"
                            style={{ background: `linear-gradient(145deg, #1a1510, #241d0a)`, border: `1.5px solid ${GOLD}40`, boxShadow: `0 32px 80px ${GOLD}20` }}
                        >
                            <div className="absolute top-0 right-0 px-5 py-2 text-[9px] font-black uppercase tracking-[0.2em] rounded-bl-2xl"
                                style={{ background: `linear-gradient(135deg, ${GOLD_DIM}, ${GOLD})`, color: DARK }}>
                                {t('elite_landing.pricing.elite.popular', '✦ Most Popular')}
                            </div>
                            <div className="absolute top-0 left-0 w-full h-full pointer-events-none"
                                style={{ background: 'radial-gradient(ellipse at 20% 0%, rgba(201,168,76,0.08) 0%, transparent 60%)' }} />

                            <div className="mb-8 relative z-10">
                                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-black px-3 py-1.5 rounded-full"
                                    style={{ color: GOLD, background: `${GOLD}15`, border: `1px solid ${GOLD}30` }}>
                                    <Crown size={10} /> {t('elite_landing.pricing.elite.badge', 'Elite Academy')}
                                </span>
                            </div>
                            <div className="flex items-baseline gap-2 mb-1 relative z-10">
                                <span className="text-6xl font-black text-white">${displayMonthly('elite')}</span>
                                <span className="font-bold" style={{ color: `${GOLD}60` }}>{t('elite_landing.pricing.mo', '/mo')}</span>
                            </div>
                            {billedLabel('elite') && <p className="text-xs font-black uppercase tracking-widest mb-6 relative z-10" style={{ color: GOLD }}>{billedLabel('elite')}</p>}
                            <p className="font-medium text-sm mb-8 leading-relaxed relative z-10" style={{ color: 'rgba(201,168,76,0.6)' }}>
                                {t('elite_landing.pricing.elite.desc', `All ${totalCourses} Elite Academy courses: etiquette, high oratory, culture, wealth architecture, and executive presence.`, { courses: totalCourses })}
                            </p>
                            <ul className="space-y-3 mb-10 flex-1 relative z-10">
                                {[
                                    t('elite_landing.pricing.elite.f1', `${totalCourses} elite curriculum courses`, { courses: totalCourses }),
                                    t('elite_landing.pricing.elite.f2', 'All 3 tiers: Foundation to Executive'),
                                    t('elite_landing.pricing.elite.f3', '20 core concepts per lesson'),
                                    t('elite_landing.pricing.elite.f4', 'Comprehensive mastery analytics'),
                                    t('elite_landing.pricing.elite.f5', 'Executive completion certificates'),
                                    t('elite_landing.pricing.elite.f6', 'Priority 1-on-1 AI mentorship')
                                ].map(f => (
                                    <li key={f} className="flex items-center gap-3 text-sm font-medium" style={{ color: `${GOLD}80` }}>
                                        <Star size={14} className="flex-shrink-0" style={{ color: GOLD }} />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <div className="flex flex-col gap-3 relative z-10">
                                <Button
                                    onClick={() => navigate(enrolPath('edu_elite'))}
                                    className="w-full h-14 rounded-2xl font-black text-base text-slate-900 active:scale-95 transition-all"
                                    style={{ background: `linear-gradient(135deg, ${GOLD_DIM}, ${GOLD})`, boxShadow: `0 8px 32px ${GOLD}30` }}
                                >
                                    {t('elite_landing.pricing.elite.cta_family', 'Enrol Family Account')} <ArrowRight className="ml-2 inline" />
                                </Button>
                                <Button
                                    onClick={() => navigate(individualPath('edu_elite'))}
                                    className="w-full h-11 rounded-2xl font-black text-sm transition-all border"
                                    style={{ borderColor: `${GOLD}30`, color: `${GOLD}90`, background: 'transparent' }}
                                >
                                    <User size={14} className="mr-1.5 inline" /> {t('elite_landing.pricing.elite.cta_individual', 'Join as Individual')}
                                </Button>
                            </div>
                        </motion.div>

                        {/* Bundle */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            whileHover={{ y: -8 }}
                            className="p-10 rounded-[2.5rem] border flex flex-col transition-all duration-500 relative overflow-hidden"
                            style={{ background: 'linear-gradient(145deg, #0d1020, #12152a)', borderColor: 'rgba(155,181,255,0.2)', boxShadow: '0 32px 80px rgba(155,181,255,0.08)' }}
                        >
                            <div className="mb-8">
                                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-black px-3 py-1.5 rounded-full"
                                    style={{ color: '#9BB5FF', background: 'rgba(155,181,255,0.1)', border: '1px solid rgba(155,181,255,0.2)' }}>
                                    <Sparkles size={10} /> {t('elite_landing.pricing.bundle.badge', 'Complete Bundle')}
                                </span>
                            </div>
                            <div className="flex items-baseline gap-2 mb-1">
                                <span className="text-6xl font-black text-white">${displayMonthly('bundle')}</span>
                                <span className="text-white/25 font-bold">{t('elite_landing.pricing.mo', '/mo')}</span>
                            </div>
                            {billedLabel('bundle') && <p className="text-xs font-black uppercase tracking-widest mb-6 text-blue-400">{billedLabel('bundle')}</p>}
                            <p className="text-white/40 font-medium text-sm mb-8 leading-relaxed">{t('elite_landing.pricing.bundle.desc', 'Everything in Standard + Elite Academy. The complete 113-course library for total academic and social distinction.')}</p>
                            <ul className="space-y-3 mb-10 flex-1">
                                {[
                                    t('elite_landing.pricing.bundle.f1', '84 standard academic subjects'),
                                    t('elite_landing.pricing.bundle.f2', `${totalCourses} elite academy courses`, { courses: totalCourses }),
                                    t('elite_landing.pricing.bundle.f3', '113 total courses'),
                                    t('elite_landing.pricing.bundle.f4', 'All 3 progressive tiers'),
                                    t('elite_landing.pricing.bundle.f5', 'Parent & individual dashboards'),
                                    t('elite_landing.pricing.bundle.f6', 'Comprehensive progress records')
                                ].map(f => (
                                    <li key={f} className="flex items-center gap-3 text-sm text-white/50 font-medium">
                                        <CheckCircle2 size={14} className="text-blue-400 flex-shrink-0" />
                                        {f}
                                    </li>
                                ))}
                            </ul>
                            <div className="flex flex-col gap-3">
                                <Button
                                    onClick={() => navigate(enrolPath('edu_bundle'))}
                                    className="w-full h-14 rounded-2xl font-black text-base text-white transition-all border active:scale-95"
                                    style={{ background: 'rgba(155,181,255,0.1)', borderColor: 'rgba(155,181,255,0.2)' }}
                                >
                                    {t('elite_landing.pricing.bundle.cta_family', 'Enrol Family Bundle')}
                                </Button>
                                <Button
                                    onClick={() => navigate(individualPath('edu_bundle'))}
                                    className="w-full h-11 rounded-2xl font-black text-sm transition-all border"
                                    style={{ borderColor: 'rgba(155,181,255,0.2)', color: 'rgba(155,181,255,0.6)', background: 'transparent' }}
                                >
                                    <User size={14} className="mr-1.5 inline" /> {t('elite_landing.pricing.bundle.cta_individual', 'Join as Individual')}
                                </Button>
                            </div>
                        </motion.div>
                    </div>

                    <p className="text-center text-white/20 text-sm mt-10 font-medium">
                        All plans include monthly, 6-monthly, or yearly billing · Cancel anytime · Sibling discounts available
                    </p>

                    <div className="mt-12 text-center">
                        <HardshipApplicationForm applicationType="elite" />
                    </div>
                </div>
            </section>

            {/* ── COMPARISON TABLE ─────────────────────────────────────────────────── */}
            <section className="py-24 px-6" style={{ background: DARK }}>
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-3xl font-black text-white text-center mb-12 tracking-tight">{t('elite_landing.comparison.title', 'Plan Comparison')}</h2>
                    <div className="rounded-[2rem] overflow-hidden border border-white/8" style={{ background: CARD }}>
                        <div className="grid grid-cols-4 bg-white/5">
                            <div className="p-6 font-black text-white/30 text-xs uppercase tracking-widest">{t('elite_landing.comparison.col_feature', 'Feature')}</div>
                            {['Standard', 'Elite', 'Bundle'].map(h => (
                                <div key={h} className="p-6 font-black text-center text-sm" style={{ color: h === 'Elite' ? GOLD : 'rgba(255,255,255,0.6)' }}>{h}</div>
                            ))}
                        </div>
                        {[
                            [t('elite_landing.comparison.row_subjects', '84 Academic Subjects'), '✓', '—', '✓'],
                            [t('elite_landing.comparison.row_elite_courses', `${totalCourses} Elite Courses`, { courses: totalCourses }), '—', '✓', '✓'],
                            [t('elite_landing.comparison.row_foundation', 'Foundation Tier (Poise & Basics)'), '✓', '✓', '✓'],
                            [t('elite_landing.comparison.row_intermediate', 'Intermediate Tier (Command & Enterprise)'), '✓', '✓', '✓'],
                            [t('elite_landing.comparison.row_advanced', 'Advanced Mastery (Diplomacy & Wealth)'), '✓', '✓', '✓'],
                            [t('elite_landing.comparison.row_etiquette', 'Etiquette & High Oratory'), '—', '✓', '✓'],
                            [t('elite_landing.comparison.row_wealth', 'Generational Wealth & Capital'), '—', '✓', '✓'],
                            [t('elite_landing.comparison.row_culture', 'Cultural Fluency & Art Market'), '—', '✓', '✓'],
                            [t('elite_landing.comparison.row_health', 'Executive Health & Vitality'), '—', '✓', '✓'],
                            [t('elite_landing.comparison.row_dashboards', 'Parent & Personal Dashboards'), '✓', '✓', '✓'],
                            [t('elite_landing.comparison.row_certificates', 'Official Mastery Certificates'), '✓', '✓', '✓'],
                            [t('elite_landing.comparison.row_billing', 'Flexible Monthly, 6-Mo & Yearly'), '✓', '✓', '✓'],
                        ].map(([feat, ...vals], ri) => (
                            <div key={ri} className="grid grid-cols-4 border-t border-white/5" style={{ background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                                <div className="p-5 text-sm text-white/50 font-medium">{feat}</div>
                                {vals.map((v, ci) => (
                                    <div key={ci} className="p-5 text-center font-black text-base"
                                        style={{ color: v === '✓' ? (ci === 1 ? GOLD : '#4ADE80') : 'rgba(255,255,255,0.1)' }}>
                                        {v}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── UPGRADE PROMPT (for existing homeschool users) ───────────────────── */}
            <section className="py-20 px-6" style={{ background: DARK2 }}>
                <div className="max-w-4xl mx-auto">
                    <div className="p-10 md:p-14 rounded-[2.5rem] border flex flex-col md:flex-row items-center gap-10"
                        style={{ background: 'linear-gradient(135deg, #1a1510, #241d0a)', borderColor: `${GOLD}30` }}>
                        <div className="flex-1">
                            <p className="text-xs uppercase tracking-[0.3em] font-black mb-3" style={{ color: GOLD }}>{t('elite_landing.upgrade.eyebrow', 'Existing Members')}</p>
                            <h3 className="text-3xl font-black text-white mb-4 tracking-tight">
                                {t('elite_landing.upgrade.title', 'Already Enrolled? Upgrade or Add Tiers Anytime.')}
                            </h3>
                            <p className="text-white/40 font-medium leading-relaxed">
                                {t('elite_landing.upgrade.desc', "If you're already enrolled in the Standard Education plan, you can upgrade to Elite Academy or the Complete Bundle at any time from your subscription portal. Upgrades are prorated instantly, and downgrading is equally straightforward.")}
                            </p>
                        </div>
                        <div className="flex-shrink-0">
                            <Button
                                onClick={() => navigate(isExistingParent ? '/homeschool/subscription' : '/homeschool/enrol')}
                                className="h-14 px-10 font-black text-base rounded-2xl text-slate-900 transition-all active:scale-95"
                                style={{ background: `linear-gradient(135deg, ${GOLD_DIM}, ${GOLD})` }}
                            >
                                {t('elite_landing.upgrade.cta', 'View My Plan')} <ArrowRight className="ml-2 inline" />
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── FAQ ──────────────────────────────────────────────────────────────── */}
            <section className="py-32 px-6 max-w-4xl mx-auto">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-black text-white tracking-tight">{t('elite_landing.faqs.title', 'Frequently Asked')}</h2>
                    <div className="h-1 w-20 mx-auto mt-4 rounded-full" style={{ background: `linear-gradient(90deg, ${GOLD_DIM}, ${GOLD})` }} />
                </div>
                <div className="space-y-3">
                    {[
                        {
                            q: t('elite_landing.faqs.q1', 'Who is Elite Academy designed for?'),
                            a: t('elite_landing.faqs.a1', 'Elite Academy was engineered for two groups: ambitious youth (ages 8–18) whose families want to equip them with the social grace, rhetoric, and financial literacy reserved for elite boarding schools, and adults/professionals who wish to master etiquette, high oratory, and wealth architecture.')
                        },
                        {
                            q: t('elite_landing.faqs.q2', 'How is this different from the Standard Homeschool curriculum?'),
                            a: t('elite_landing.faqs.a2', 'Our Standard curriculum covers 84 traditional academic subjects (Mathematics, Sciences, Literature, History, Languages). Elite Academy contains 30 distinct courses teaching the non-academic curriculum of top institutions: diplomatic protocol, capital markets, fine art curation, classical rhetoric, equestrian & high culture, and family office wealth management.')
                        },
                        {
                            q: t('elite_landing.faqs.q3', 'Can I access both Standard and Elite courses?'),
                            a: t('elite_landing.faqs.a3', 'Yes. The Complete Bundle ($59/mo) gives full access to all 84 Standard academic subjects and all 30 Elite Academy courses in a single unified dashboard.')
                        },
                        {
                            q: t('elite_landing.faqs.q4', 'How does the 1-on-1 AI mentorship work?'),
                            a: t('elite_landing.faqs.a4', 'Each course is guided by Sage AI in real-time. Sage adapts dialogue, asks probing Socratic questions, conducts simulated negotiation scenarios, evaluates speech and etiquette etiquette exercises, and grades mastery quizzes dynamically 24/7.')
                        },
                        {
                            q: t('elite_landing.faqs.q5', 'Can I switch between plans or cancel?'),
                            a: t('elite_landing.faqs.a5', 'Absolutely. You can upgrade, downgrade, or cancel your subscription at any time with a single click in your portal. Upgrades are instantly prorated so you only pay for what you use.')
                        }
                    ].map((faq, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.07 }}
                            className="rounded-[1.5rem] overflow-hidden border transition-colors"
                            style={{ background: CARD, borderColor: openFaq === i ? `${GOLD}30` : 'rgba(255,255,255,0.05)' }}
                        >
                            <button
                                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                className="w-full p-7 flex items-center justify-between text-left"
                            >
                                <span className="font-black text-white pr-8 leading-snug">{faq.q}</span>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${openFaq === i ? 'rotate-45' : ''}`}
                                    style={{ background: openFaq === i ? GOLD : 'rgba(255,255,255,0.05)', color: openFaq === i ? DARK : 'rgba(255,255,255,0.3)' }}>
                                    <Plus size={16} strokeWidth={3} />
                                </div>
                            </button>
                            <AnimatePresence>
                                {openFaq === i && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                                    >
                                        <div className="px-7 pb-7 text-white/40 font-medium leading-relaxed border-t border-white/5 pt-5 mx-4">
                                            {faq.a}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ── FINAL CTA ────────────────────────────────────────────────────────── */}
            <section className="py-32 px-6 text-center relative overflow-hidden" style={{ background: DARK2 }}>
                <div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(201,168,76,0.07) 0%, transparent 65%)' }} />
                <div className="max-w-4xl mx-auto relative z-10">
                    <Crown size={48} className="mx-auto mb-8 opacity-60" style={{ color: GOLD }} />
                    <h2 className="text-5xl md:text-7xl font-black text-white tracking-tighter leading-none mb-6">
                        {t('elite_landing.final_cta.title_part1', 'Claim the')}<br />
                        <span style={{ background: `linear-gradient(90deg, ${GOLD_LIGHT}, ${GOLD})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            {t('elite_landing.final_cta.title_part2', 'Elite Advantage.')}
                        </span>
                    </h2>
                    <p className="text-xl text-white/30 font-bold mb-12 uppercase tracking-[0.15em]">
                        {t('elite_landing.final_cta.tagline', 'The curriculum that shapes leaders. For your family, or for yourself.')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button
                            onClick={() => navigate(enrolPath('edu_elite'))}
                            size="xl"
                            className="h-20 px-14 text-xl font-black rounded-[2rem] text-slate-900 active:scale-95 transition-all"
                            style={{ background: `linear-gradient(135deg, ${GOLD_DIM}, ${GOLD}, #a07820)`, boxShadow: `0 24px 80px ${GOLD}30` }}
                        >
                            {t('elite_landing.final_cta.enrolFamily', 'Enrol Your Family')} <ArrowRight className="ml-3 inline" size={24} />
                        </Button>
                        <Button
                            onClick={() => navigate(individualPath('edu_elite'))}
                            size="xl"
                            className="h-20 px-14 text-xl font-black rounded-[2rem] transition-all border"
                            style={{ borderColor: `${GOLD}40`, color: GOLD, background: `${GOLD}08` }}
                        >
                            <User size={22} className="mr-2 inline" /> {t('elite_landing.final_cta.joinIndividual', 'Join as an Individual')}
                        </Button>
                    </div>
                    <div className="mt-8">
                        <Button
                            variant="outline"
                            onClick={() => navigate('/sponsor')}
                            className="h-14 px-8 text-xs font-black uppercase tracking-widest text-amber-500 border-amber-500/30 hover:bg-amber-500/10 rounded-2xl"
                        >
                            {t('elite_landing.final_cta.sponsor', '💛 Sponsor a student for Elite Academy →')}
                        </Button>
                    </div>
                    <p className="mt-6 text-white/15 text-sm font-medium">{t('elite_landing.final_cta.footer_note', 'Cancel anytime · No setup fee · Instant access · Monthly, 6-monthly & yearly billing')}</p>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-10 px-6 border-t" style={{ background: DARK, borderColor: 'rgba(255,255,255,0.04)' }}>
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-3 opacity-40">
                        <Crown size={16} style={{ color: GOLD }} />
                        <span className="text-xs font-bold tracking-widest uppercase text-white">© 2026 Still Waters Elite Academy · Still Waters</span>
                    </div>
                    <div className="flex flex-wrap gap-8 text-[10px] font-black uppercase tracking-widest text-white/20">
                        <Link to="/homeschool" className="hover:text-white/50 transition-colors">{t('elite.standard_school')}</Link>
                        <Link to="/sponsor" className="hover:text-white/50 transition-colors">Sponsorship</Link>
                        <Link to="/ngo" className="hover:text-white/50 transition-colors">{t('elite.ngo_partners')}</Link>
                        <Link to="/privacy" className="hover:text-white/50 transition-colors">Privacy</Link>
                        <Link to="/terms" className="hover:text-white/50 transition-colors">Terms</Link>
                    </div>
                </div>
            </footer>

            {/* Course Detail Modal */}
            <CourseDetailModal
                course={detailCourse}
                onClose={() => setDetailCourse(null)}
                onEnrol={() => {
                    setDetailCourse(null);
                    navigate(enrolPath('edu_elite'));
                }}
            />
        </div>
    );
}
